import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import store from "../../../app/store";
import { calculateAPValue } from "../../../utils/Utils";
import { setSelectedBankMetricData } from "../infiniteBankPerformanceMetricsSlice/infiniteBankPerformanceMetricsSlice";
import { HttpStatusCode } from "axios";

const _authToken = () => {
  const state = store.getState();
  return `${state?.authReducer?.userData?.tokenType?.toLowerCase()} ${
    state?.authReducer?.userData?.token
  }`;
};
const _orgClientId = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.orgClientId
    ? state?.orgReducer?.selectedFI?.orgClientId
    : state?.authReducer?.userData?.client_Id;
};
const _idrssd = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.idrssd;
};

const buildCustomErrorResponse = (error, status = "CUSTOM_ERROR") => ({
  error: {
    status,
    error,
  },
});

const getSettledRequestError = (result, requestName) => {
  if (result?.status === "rejected") {
    return {
      status: "CUSTOM_ERROR",
      error: result?.reason || `${requestName} request failed`,
    };
  }

  if (result?.value?.error) {
    return {
      status: result?.value?.error?.status || "CUSTOM_ERROR",
      error: result?.value?.error,
    };
  }

  if (result?.value?.data?.statusCode !== HttpStatusCode.Ok) {
    return {
      status: result?.value?.data?.statusCode || "CUSTOM_ERROR",
      error: result?.value?.data?.message || `${requestName} request failed`,
    };
  }

  return null;
};

// ---------------------------------------------------------
// RTK Query API
// ---------------------------------------------------------
export const downloadFHSReportRTK = createApi({
  reducerPath: "downloadFHSReportRTK",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ["DownloadReportQuarterList", "FHSDataBasedOnQuarter"],
  endpoints: (builder) => ({
    // ----------------------------------------
    // GET QUARTER LIST
    // ----------------------------------------
    getDownloadReportQuarterList: builder.query({
      query: () => ({
        url: `/bank/${_idrssd()}/fhs/quarter`,
        method: "GET",
        headers: {
          Authorization: _authToken(),
          "Content-Type": "application/json",
        },
      }),
      transformResponse: (response) => {
        let dropdownItems = response?.data?.map((item, index) => ({
          label: item,
          code: index === 0 ? "current" : item,
        }));
        return { data: [...dropdownItems] };
      },
      providesTags: ["DownloadReportQuarterList"],
    }),

    // ----------------------------------------
    // GET FHS DATA BASED ON QUARTER
    // ----------------------------------------
    getFHSDataBasedOnQuarter: builder.query({
      async queryFn({ quarter }, _queryApi, _extraOptions, fetchWithBQ) {
        const headers = {
          Authorization: _authToken(),
          "Content-Type": "application/json",
        };
        try {
          const [fhsResult, ewiResult, cohortResult] = await Promise.allSettled(
            [
              fetchWithBQ({
                url: `bank/${_orgClientId()}/fhs/${_idrssd()}?quarter=${quarter}`,
                method: "GET",
                headers,
              }),
              fetchWithBQ({
                url: `bank/${_orgClientId()}/ewi/${_idrssd()}?quarter=${quarter}`,
                method: "GET",
                headers,
              }),
              fetchWithBQ({
                url: `/bank/${_idrssd()}/cohort?PageNumber=1&PageSize=10&isdashboard=true&quarter=${quarter}`,
                method: "GET",
                headers,
              }),
            ],
          );

          const requestError =
            getSettledRequestError(fhsResult, "FHS") ||
            getSettledRequestError(ewiResult, "EWI") ||
            getSettledRequestError(cohortResult, "Cohort");

          if (requestError) {
            return buildCustomErrorResponse(
              requestError.error,
              requestError.status,
            );
          }

          const fhsData = fhsResult?.value?.data?.data;
          const ewiData = ewiResult?.value?.data?.data;
          const cohortData = cohortResult?.value?.data?.data;

          const finalData = {
            ...fhsData,
            cohortList: cohortData ?? null,

            adjusted_metrics: ewiData?.adjusted_metrics,
            early_warning_indicators: ewiData?.early_warning_indicators,

            profitability: calculateAPValue(
              fhsData?.profitability,
              ewiData?.adjusted_metrics,
            ),

            growth_metrics: calculateAPValue(
              fhsData?.growth_metrics,
              ewiData?.adjusted_metrics,
            ),

            additional_regulatory_and_risk_metrics: calculateAPValue(
              fhsData?.additional_regulatory_and_risk_metrics,
              ewiData?.adjusted_metrics,
            ),
          };

          _queryApi.dispatch(setSelectedBankMetricData(finalData));

          return {
            data: finalData,
          };
        } catch (error) {
          return buildCustomErrorResponse(error);
        }
      },

      providesTags: ["FHSDataBasedOnQuarter"],
    }),
  }),
});

export const {
  useGetDownloadReportQuarterListQuery,
  useGetFHSDataBasedOnQuarterQuery,
} = downloadFHSReportRTK;
